import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { OcrQuotaSummary, OcrQuotaSummarySchema } from '../schemas'

export async function getOcrQuota(): Promise<OcrQuotaSummary> {
  const transactionId = nanoid()
  const url = `${kPaxtonAppApiRESTBaseUrl()}/api/v1/ocr-quota`

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) {
      throw new Error('User not authenticated')
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': transactionId,
      },
    })
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) {
      const detail = !parseError && data && typeof data === 'object' && 'detail' in data ? String(data.detail) : `Failed to fetch OCR quota: ${response.status}`

      throw new ApiError(response.status, detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(new Error('OCR quota response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            parseError: formatUnknownError(parseError),
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid OCR quota response JSON')
    }

    const parsed = OcrQuotaSummarySchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(new Error('OCR quota response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid OCR quota response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(error, {
          extra: { requestUrl: url },
        })
      })
    }
    throw error
  }
}
